import { useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AdminUser,
  type AuditEventRow,
  type AuditReport,
  type GroupInfo,
  type OidcConfigView,
  type Organization,
  type RecoveryStatus,
} from "../api/client";
import { useVault } from "../vault/store";
import { b64decode, b64encode, wrapOrgKeyTo, unwrapOrgKey } from "../crypto/vault";
import { AccessPanel } from "./AccessPanel";
import { KeyDistribution } from "./KeyDistribution";
import { grantAndDistribute } from "../vault/access-keys";

type Tab = "access" | "sso" | "users" | "roles" | "groups" | "recovery" | "audit";

export function Admin({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("access");
  const tabs: [Tab, string][] = [
    ["access", "Access"],
    ["users", "Users"],
    ["roles", "Advanced grants"],
    ["groups", "Groups"],
    ["recovery", "Recovery"],
    ["sso", "Single Sign-On"],
    ["audit", "Audit"],
  ];
  return (
    <div className="flex min-h-0 flex-1">
      <nav className="flex w-56 flex-col border-r border-border bg-panel">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-[11px] font-semibold tracking-wide text-muted">ADMINISTRATION</span>
          <button onClick={onClose} title="Back to vault" className="rounded px-1.5 text-muted hover:bg-panel2 hover:text-fg">
            ✕
          </button>
        </div>
        {tabs.map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-left hover:bg-panel2 ${tab === t ? "bg-accent/20" : ""}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <main className="min-h-0 flex-1 overflow-auto p-5">
        {tab === "access" && <AccessPanel />}
        {tab === "sso" && <SsoPanel />}
        {tab === "users" && <UsersPanel />}
        {tab === "roles" && <RolesPanel />}
        {tab === "groups" && <GroupsPanel />}
        {tab === "recovery" && <RecoveryPanel />}
        {tab === "audit" && <AuditPanel />}
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6 max-w-2xl rounded-md border border-border bg-panel">
      <div className="border-b border-border px-4 py-2.5 font-semibold">{title}</div>
      <div className="flex flex-col gap-3 px-4 py-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted">{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
      />
    </label>
  );
}

function Btn({ children, onClick, busy, disabled, title }: { children: React.ReactNode; onClick: () => void; busy?: boolean; disabled?: boolean; title?: string }) {
  return (
    <button
      onClick={onClick}
      disabled={busy || disabled}
      title={title}
      className="self-start rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-60"
    >
      {busy ? "Working…" : children}
    </button>
  );
}

const REDIRECT_HINT = `${location.origin}/api/v1/auth/oidc/callback`;

function SsoPanel() {
  const [cfg, setCfg] = useState<OidcConfigView | null>(null);
  const [tenant, setTenant] = useState("");
  const [client, setClient] = useState("");
  const [redirect, setRedirect] = useState(REDIRECT_HINT);
  const [enabled, setEnabled] = useState(false);
  const [pem, setPem] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getOidcConfig().then((c) => {
      setCfg(c);
      setTenant(c.tenant_id);
      setClient(c.client_id);
      setRedirect(c.redirect_uri || REDIRECT_HINT);
      setEnabled(c.enabled);
    }).catch(() => undefined);
  }, []);

  async function save() {
    setBusy(true); setMsg(null);
    try {
      const c = await api.putOidcConfig({ enabled, tenant_id: tenant, client_id: client, redirect_uri: redirect });
      setCfg(c);
      setMsg("Saved.");
    } catch (e) { setMsg(e instanceof ApiError ? e.message : "Save failed"); } finally { setBusy(false); }
  }
  async function genCert() {
    setBusy(true); setMsg(null);
    try {
      const c = await api.generateCert();
      setPem(c.certificate_pem);
      setCfg((p) => (p ? { ...p, has_certificate: true, cert_thumbprint: c.thumbprint } : p));
      setMsg(`Certificate generated (thumbprint ${c.thumbprint}). Upload the PEM below to your Entra app registration.`);
    } catch (e) { setMsg(e instanceof ApiError ? e.message : "Failed"); } finally { setBusy(false); }
  }

  return (
    <>
      <Section title="Entra ID (certificate authentication)">
        <p className="text-xs text-muted">
          Authentication to Entra uses a <b>certificate</b> (private_key_jwt), not a client secret.
          Generate a certificate, upload the public PEM to your Entra app registration, then set the
          tenant/client IDs and enable SSO.
        </p>
        <Field label="Tenant ID" value={tenant} onChange={setTenant} placeholder="contoso.onmicrosoft.com or GUID" />
        <Field label="Client (application) ID" value={client} onChange={setClient} />
        <Field label="Redirect URI" value={redirect} onChange={setRedirect} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enable single sign-on
        </label>
        <div className="text-xs text-muted">
          Status: {cfg?.has_certificate ? `certificate present${cfg.cert_thumbprint ? ` (x5t ${cfg.cert_thumbprint})` : ""}` : "no certificate yet"}
        </div>
        <div className="flex gap-2">
          <Btn onClick={save} busy={busy}>Save configuration</Btn>
          <button onClick={genCert} disabled={busy} className="rounded border border-border px-3 py-1.5 hover:border-accent disabled:opacity-60">
            Generate certificate
          </button>
        </div>
        {msg && <div className="text-xs text-accent">{msg}</div>}
      </Section>
      {pem && (
        <Section title="Public certificate (upload to Entra)">
          <textarea readOnly value={pem} rows={10} className="rounded border border-border bg-panel2 px-2 py-1.5 font-mono text-xs" />
          <button onClick={() => navigator.clipboard.writeText(pem)} className="self-start rounded border border-border px-3 py-1.5 hover:border-accent">
            Copy PEM
          </button>
        </Section>
      )}
    </>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [login, setLogin] = useState("");
  const [sso, setSso] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [grantFor, setGrantFor] = useState<AdminUser | null>(null);

  async function refresh() {
    try {
      setUsers((await api.listUsers()).users);
    } catch {
      /* ignore */
    }
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.listUsers();
        if (alive) setUsers(r.users);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function create() {
    if (!login.trim()) {
      setMsg("Enter a login (email / UPN)");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.createUser(login.trim(), sso);
      setLogin("");
      setMsg("User created. They can now sign in with Entra (account links on first login).");
      await refresh();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function toggleSso(u: AdminUser) {
    try {
      await api.setUserSso(u.id, !u.sso_enabled);
      await refresh();
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <Section title="Add user (SSO)">
        <p className="text-xs text-muted">
          Pre-provision an account by its Entra login (UPN / email). With no password it is
          SSO-only and links automatically on the user's first Entra sign-in. Grant them a role
          under “Roles & Grants” and add them to organizations under “Members”.
        </p>
        <Field label="Login (e.g. mhausknecht@itgrow.cz)" value={login} onChange={setLogin} placeholder="user@itgrow.cz" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={sso} onChange={(e) => setSso(e.target.checked)} />
          Enable SSO (Entra)
        </label>
        <Btn onClick={create} busy={busy}>Create user</Btn>
        {msg && <div className="text-xs text-accent">{msg}</div>}
      </Section>
      <div className="max-w-4xl rounded-md border border-border bg-panel">
        <div className="border-b border-border px-4 py-2.5 font-semibold">Users ({users.length})</div>
        <div className="grid grid-cols-[2fr_0.8fr_0.8fr_0.6fr_1fr] border-b border-border px-4 py-1.5 text-[11px] font-semibold text-muted">
          <div>Login</div><div>Auth</div><div>Enrolled</div><div>SSO</div><div>Access</div>
        </div>
        {users.map((u) => (
          <div key={u.id} className="grid grid-cols-[2fr_0.8fr_0.8fr_0.6fr_1fr] items-center px-4 py-1.5 text-xs">
            <div className="truncate">{u.login || u.oidc_subject || u.id}{u.is_break_glass ? " 🛟" : ""}</div>
            <div className="text-muted">{u.auth_type}</div>
            <div className="text-muted">{u.enrolled ? "yes" : "no"}</div>
            <div>
              <button onClick={() => toggleSso(u)} className={`rounded px-2 py-0.5 ${u.sso_enabled ? "bg-ok/20 text-ok" : "border border-border text-muted"}`}>
                {u.sso_enabled ? "on" : "off"}
              </button>
            </div>
            <div>
              <button
                onClick={() => setGrantFor(u)}
                disabled={!u.enrolled}
                title={u.enrolled ? "Grant access to organizations" : "User must enroll first"}
                className="rounded border border-border px-2 py-0.5 hover:border-accent disabled:opacity-40"
              >
                Grant access…
              </button>
            </div>
          </div>
        ))}
      </div>
      {grantFor && <BulkAccessDialog user={grantFor} onClose={() => setGrantFor(null)} />}
    </>
  );
}

// BulkAccessDialog grants one user access to many organizations at once: for each selected
// org the org_key is re-wrapped onto the user's pubkey in the admin's browser (§7) and a
// role is granted. This is how a new technician is onboarded to N clients in one action.
export function BulkAccessDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const vault = useVault();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [role, setRole] = useState("technician");
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listOrgs().then((r) => setOrgs(r.organizations.filter((o) => !o.is_personal))).catch(() => undefined);
  }, []);

  const allChecked = orgs.length > 0 && orgs.every((o) => picked[o.id]);
  function toggleAll() {
    const next: Record<string, boolean> = {};
    if (!allChecked) orgs.forEach((o) => (next[o.id] = true));
    setPicked(next);
  }

  async function apply() {
    if (!user.public_key) {
      setLog(["User has no public key (not enrolled)."]);
      return;
    }
    const targets = orgs.filter((o) => picked[o.id]);
    if (targets.length === 0) {
      setLog(["Select at least one organization."]);
      return;
    }
    setBusy(true);
    setLog([`Granting ${user.login} access to ${targets.length} org(s)…`]);
    const pub = b64decode(user.public_key);
    let ok = 0;
    for (const o of targets) {
      try {
        const orgKey = vault.orgKeyFor(o); // admin must be a member to unwrap
        await api.addMember(o.id, user.id, b64encode(wrapOrgKeyTo(pub, orgKey)));
        if (role !== "none") {
          await api.createGrant({ principal_type: "user", principal_id: user.id, scope: "org", node_id: o.id, role });
        }
        ok++;
        setLog((l) => [...l, `✓ ${o.name}`]);
      } catch {
        setLog((l) => [...l, `✗ ${o.name} (you may not be a member / cannot unwrap its key)`]);
      }
    }
    setLog((l) => [...l, `Done: ${ok}/${targets.length} granted.`]);
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 grid place-items-center bg-black/50" onClick={onClose}>
      <div className="flex max-h-[80vh] w-[480px] flex-col rounded-md border border-border bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 font-semibold">Grant access — {user.login}</div>
        <div className="flex flex-col gap-3 px-4 py-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted">Organizations</span>
            <button onClick={toggleAll} className="text-xs text-accent hover:underline">{allChecked ? "clear all" : "select all"}</button>
          </div>
          <div className="max-h-48 overflow-auto rounded border border-border">
            {orgs.map((o) => (
              <label key={o.id} className="flex items-center gap-2 px-2 py-1 hover:bg-panel2">
                <input type="checkbox" checked={!!picked[o.id]} onChange={(e) => setPicked((p) => ({ ...p, [o.id]: e.target.checked }))} />
                {o.name}
              </label>
            ))}
            {orgs.length === 0 && <div className="px-2 py-2 text-xs text-muted">No organizations you administer.</div>}
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
              {ORG_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              <option value="none">membership only (no role)</option>
            </select>
          </label>
          {log.length > 0 && (
            <div className="max-h-32 overflow-auto rounded bg-panel2 p-2 font-mono text-xs">
              {log.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 hover:border-accent">Close</button>
          <button onClick={apply} disabled={busy} className="rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-60">
            {busy ? "Applying…" : "Grant access"}
          </button>
        </div>
      </div>
    </div>
  );
}

const ROLES = ["owner_admin", "org_manager", "technician", "launch_only", "auditor"];

function RolesPanel() {
  const vault = useVault();
  const [log, setLog] = useState<string[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [principalType, setPrincipalType] = useState<"user" | "group">("user");
  const [userId, setUserId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [role, setRole] = useState("technician");
  const [scope, setScope] = useState("org");
  const [nodeId, setNodeId] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listUsers().then((r) => setUsers(r.users)).catch(() => undefined);
    api.listGroups().then((r) => setGroups(r.groups)).catch(() => undefined);
    api.listOrgs().then((r) => setOrgs(r.organizations.filter((o) => !o.is_personal))).catch(() => undefined);
  }, []);

  async function grant() {
    const principalId = principalType === "user" ? userId : groupId;
    if (!principalId) { setMsg(`Pick a ${principalType}`); return; }
    if (scope === "org" && !nodeId) { setMsg("Pick an organization"); return; }
    setBusy(true); setMsg(null); setLog([]);
    try {
      // A grant alone is authorization; the vault only becomes visible once its key is wrapped
      // to each affected person. Both happen here, in one action — a group grant without the
      // second half is a silent no-op (see KeyDistribution).
      const affected = principalType === "group"
        ? (await api.listGroupMembers(principalId)).members.map((m) => m.user_id)
        : [principalId];
      const res = await grantAndDistribute({
        grant: {
          principal_type: principalType,
          principal_id: principalId,
          scope,
          node_id: scope === "instance" ? undefined : nodeId,
          role,
        },
        userIds: affected,
        orgKeyFor: vault.orgKeyFor,
        log: (l) => setLog((p) => [...p, l]),
      });
      setMsg(res.blocked > 0
        ? `Grant created, ${res.ok} key(s) distributed — ${res.blocked} pending (see Access → Keys).`
        : `Grant created, ${res.ok} key(s) distributed.`);
    } catch (e) { setMsg(e instanceof ApiError ? e.message : "Failed"); } finally { setBusy(false); }
  }

  return (
    <Section title="Assign a role">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">Principal</span>
        <select value={principalType} onChange={(e) => setPrincipalType(e.target.value as "user" | "group")} className="rounded border border-border bg-panel2 px-2 py-1.5">
          <option value="user">user</option>
          <option value="group">group</option>
        </select>
      </label>
      {principalType === "user" ? (
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">User</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
            <option value="">— select —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.login || u.oidc_subject || u.id}</option>)}
          </select>
        </label>
      ) : (
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Group</span>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
            <option value="">— select —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">Scope</span>
        <select value={scope} onChange={(e) => setScope(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
          <option value="instance">instance</option>
          <option value="org">organization</option>
        </select>
      </label>
      {scope === "org" && (
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Organization</span>
          <select value={nodeId} onChange={(e) => setNodeId(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
            <option value="">— select —</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      )}
      {vault.phase !== "unlocked" && (
        <div className="rounded border border-warn/40 bg-warn/10 p-2 text-xs text-warn">
          Unlock the vault first — assigning a role also hands the vault key to the people it
          affects, which needs your own key to unwrap it.
        </div>
      )}
      <Btn
        onClick={grant}
        busy={busy}
        disabled={vault.phase !== "unlocked"}
        title={vault.phase !== "unlocked" ? "Unlock the vault first" : undefined}
      >
        Create grant &amp; distribute keys
      </Btn>
      {msg && <div className="text-xs text-accent">{msg}</div>}
      {log.length > 0 && (
        <div className="max-h-40 overflow-auto rounded bg-panel2 p-2 font-mono text-xs">
          {log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
      {log.length > 0 && (
        <div className="mt-2 border-t border-border pt-3">
          <div className="mb-2 text-xs text-muted">Still waiting for a key:</div>
          <KeyDistribution compact />
        </div>
      )}
    </Section>
  );
}

function GroupsPanel() {
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [name, setName] = useState("");
  const [sel, setSel] = useState("");
  const [members, setMembers] = useState<{ user_id: string; login: string }[]>([]);
  const [addUser, setAddUser] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => api.listGroups().then((r) => setGroups(r.groups)).catch(() => undefined);
  useEffect(() => {
    void reload();
    api.listUsers().then((r) => setUsers(r.users)).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!sel) return;
    api.listGroupMembers(sel).then((r) => setMembers(r.members)).catch(() => setMembers([]));
  }, [sel]);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.createGroup(name.trim()); setName(""); await reload(); } finally { setBusy(false); }
  }
  const refreshMembers = () => sel && api.listGroupMembers(sel).then((r) => setMembers(r.members)).catch(() => undefined);

  return (
    <Section title="Groups">
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name" className="flex-1 rounded border border-border bg-panel2 px-2 py-1.5" />
        <Btn onClick={create} busy={busy}>Create</Btn>
      </div>
      <div className="rounded border border-border">
        {groups.map((g) => (
          <div key={g.id} className={`flex items-center gap-2 px-2 py-1.5 ${sel === g.id ? "bg-accent/20" : "hover:bg-panel2"}`}>
            <button onClick={() => setSel(g.id)} className="flex-1 text-left">{g.name} <span className="text-xs text-muted">· {g.member_count}</span></button>
            <button onClick={async () => { if (window.confirm("Delete group?")) { await api.deleteGroup(g.id); if (sel === g.id) setSel(""); await reload(); } }} className="text-xs text-danger hover:underline">delete</button>
          </div>
        ))}
        {groups.length === 0 && <div className="px-2 py-2 text-xs text-muted">No groups yet.</div>}
      </div>
      {sel && (
        <div className="rounded border border-border p-2">
          <div className="mb-1 text-xs font-semibold text-muted">MEMBERS</div>
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-2 py-0.5">
              <span className="flex-1 truncate">{m.login || m.user_id}</span>
              <button onClick={async () => { await api.removeGroupMember(sel, m.user_id); refreshMembers(); }} className="text-xs text-danger hover:underline">remove</button>
            </div>
          ))}
          <div className="mt-2 flex gap-2">
            <select value={addUser} onChange={(e) => setAddUser(e.target.value)} className="flex-1 rounded border border-border bg-panel2 px-2 py-1">
              <option value="">— add user —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.login || u.oidc_subject || u.id}</option>)}
            </select>
            <button onClick={async () => { if (addUser) { await api.addGroupMember(sel, addUser); setAddUser(""); refreshMembers(); void reload(); } }} className="rounded border border-border px-3 py-1 text-xs hover:border-accent">Add</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function RecoveryPanel() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [orgId, setOrgId] = useState("");
  const [status, setStatus] = useState<RecoveryStatus | null>(null);
  const [recKey, setRecKey] = useState("");
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listOrgs().then((r) => setOrgs(r.organizations.filter((o) => !o.is_personal))).catch(() => undefined);
    api.listUsers().then((r) => setUsers(r.users)).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!orgId) return;
    api.getRecovery(orgId).then(setStatus).catch(() => setStatus(null));
  }, [orgId]);

  async function recover() {
    setMsg(null);
    if (!orgId || !recKey.trim() || !targetId) { setMsg("Org, recovery key and target user are required."); return; }
    if (!reason.trim()) { setMsg("A reason is required (audited)."); return; }
    const target = users.find((u) => u.id === targetId);
    if (!target?.public_key) { setMsg("Target user has no public key (not enrolled)."); return; }
    setBusy(true);
    try {
      const st = await api.getRecovery(orgId);
      if (!st.has_recovery || !st.wrapped_org_key) { setMsg("No recovery wrap for this org."); return; }
      const orgKey = unwrapOrgKey(b64decode(recKey.trim()), b64decode(st.wrapped_org_key));
      const rewrapped = wrapOrgKeyTo(b64decode(target.public_key), orgKey);
      await api.addMember(orgId, target.id, b64encode(rewrapped));
      await api.recordEvent({ event_type: "recovery_used", item_id: orgId, reason: reason.trim() });
      setMsg(`✓ Recovered: ${target.login || target.id} now has access to this vault.`);
      setRecKey("");
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Recovery failed — wrong recovery key for this org?");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Break-glass recovery (§17.1)">
      <p className="text-xs text-muted">
        Use the offline org recovery key to restore a user's access (e.g. they forgot their master
        password). Everything happens in your browser; the recovery key never leaves it.
      </p>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">Organization</span>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
          <option value="">— select —</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </label>
      {status && (
        <div className="text-xs text-muted">
          {status.opt_out ? "Recovery is opted out for this org." : status.has_recovery ? "Recovery key configured ✓" : "No recovery key configured."}
        </div>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">Recovery private key (paste offline key)</span>
        <textarea value={recKey} onChange={(e) => setRecKey(e.target.value)} className="h-20 resize-none rounded border border-border bg-panel2 px-2 py-1 font-mono text-[11px]" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">Restore access for</span>
        <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5">
          <option value="">— select user —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.login || u.oidc_subject || u.id}</option>)}
        </select>
      </label>
      <Field label="Reason (audited)" value={reason} onChange={setReason} placeholder="why recovery is needed" />
      <Btn onClick={recover} busy={busy}>Recover access</Btn>
      {msg && <div className="text-xs text-accent">{msg}</div>}
    </Section>
  );
}

const ORG_ROLES = ["org_manager", "technician", "launch_only", "auditor"];

function AuditPanel() {
  const [report, setReport] = useState<AuditReport | null>(null);
  const [head, setHead] = useState<{ seq: number; head_hash: string } | null>(null);
  const [events, setEvents] = useState<AuditEventRow[]>([]);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    try {
      setReport(await api.auditVerify());
      setHead(await api.auditHead());
      setEvents((await api.auditExport()).events);
    } catch { /* ignore */ } finally { setBusy(false); }
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rep = await api.auditVerify();
        const hd = await api.auditHead();
        const ev = (await api.auditExport()).events;
        if (alive) {
          setReport(rep);
          setHead(hd);
          setEvents(ev);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function seal() {
    setBusy(true);
    try { await api.auditSeal(); await refresh(); } catch { /* ignore */ } finally { setBusy(false); }
  }

  return (
    <>
      <Section title="Integrity">
        {report && (
          <div className={`text-sm ${report.valid ? "text-ok" : "text-danger"}`}>
            {report.valid
              ? `✓ Chain valid — ${report.events_checked} sealed events`
              : `✗ INVALID at seq ${report.first_bad_seq}: ${report.reason}`}
          </div>
        )}
        {head && <div className="text-xs text-muted">Head seq {head.seq} · {head.head_hash.slice(0, 24)}…</div>}
        <div className="flex gap-2">
          <Btn onClick={seal} busy={busy}>Seal now</Btn>
          <button onClick={refresh} disabled={busy} className="rounded border border-border px-3 py-1.5 hover:border-accent disabled:opacity-60">
            Refresh
          </button>
        </div>
      </Section>
      <div className="max-w-4xl rounded-md border border-border bg-panel">
        <div className="border-b border-border px-4 py-2.5 font-semibold">Events ({events.length})</div>
        <div className="grid grid-cols-[60px_1.5fr_1fr_1.5fr_1fr] border-b border-border px-4 py-1.5 text-[11px] font-semibold text-muted">
          <div>Seq</div><div>Event</div><div>Result</div><div>Time</div><div>Hash</div>
        </div>
        <div className="max-h-[50vh] overflow-auto">
          {events.map((e) => (
            <div key={e.id} className="grid grid-cols-[60px_1.5fr_1fr_1.5fr_1fr] px-4 py-1 text-xs">
              <div className="text-muted">{e.seq}</div>
              <div>{e.event_type}</div>
              <div className={e.result === "success" ? "" : "text-warn"}>{e.result}</div>
              <div className="text-muted">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
              <div className="truncate font-mono text-muted">{e.hash ? e.hash.slice(0, 16) : "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
